import React from 'react';
import Select from 'react-select';
import { useTheme, Box } from '@chakra-ui/react';

// Custom styles to match Chakra UI theme
const customStyles = (theme) => ({
  control: (provided, state) => ({
    ...provided,
    backgroundColor: 'white',
    borderColor: state.isFocused ? '#04385D' : theme.colors.gray[200],
    borderWidth: '1px',
    borderRadius: theme.radii.md,
    boxShadow: state.isFocused ? `0 0 0 1px #04385D` : 'none',
    '&:hover': {
      borderColor: state.isFocused ? '#04385D' : theme.colors.gray[300],
    },
    minHeight: '44px',
    fontSize: '14px',
    width: '100%',
    maxWidth: '100%',
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '2px 8px',
  }),
  input: (provided) => ({
    ...provided,
    fontSize: '14px',
    color: theme.colors.gray[900],
  }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: '14px',
    color: theme.colors.gray[500],
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: '14px',
    color: theme.colors.gray[900],
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: 'white',
    border: `1px solid ${theme.colors.gray[200]}`,
    borderRadius: theme.radii.md,
    boxShadow: theme.shadows.lg,
    zIndex: 9999,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: '200px',
    padding: '4px 0',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#04385D'
      : state.isFocused
      ? theme.colors.gray[50]
      : 'white',
    color: state.isSelected ? 'white' : theme.colors.gray[900],
    fontSize: '14px',
    padding: '8px 12px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: state.isSelected ? '#04385D' : theme.colors.gray[50],
    },
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: 'none',
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: theme.colors.gray[400],
    '&:hover': {
      color: theme.colors.gray[600],
    },
    transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
    transition: 'transform 0.2s ease',
  }),
  clearIndicator: (provided) => ({
    ...provided,
    color: theme.colors.gray[400],
    '&:hover': {
      color: theme.colors.gray[600],
    },
  }),
});

const SearchableSelect = ({ options, value, onChange, placeholder, isDisabled = false, ...props }) => {
  const theme = useTheme();

  // Convert options to react-select format if needed
  const formattedOptions = options?.map(option => ({
    value: typeof option === 'string' ? option : option.value,
    label: typeof option === 'string' ? option : option.label,
  })) || [];

  // Find the selected option
  const selectedOption = formattedOptions.find(option => option.value === value) || null;

  const handleChange = (selectedOption) => {
    onChange(selectedOption ? selectedOption.value : '');
  };

  return (
    <Box w="100%" maxW="100%" minW={0}>
      <Select
        styles={customStyles(theme)}
        options={formattedOptions}
        value={selectedOption}
        onChange={handleChange}
        placeholder={placeholder}
        isDisabled={isDisabled}
        isSearchable={true}
        isClearable={false}
        menuPortalTarget={document.body}
        {...props}
      />
    </Box>
  );
};

export default SearchableSelect;